import type { UseFormReturn } from 'react-hook-form';
import { format, parseISO } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { FormControl, FormDescription, FormField, FormItem } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import type { ItemFormData } from './itemFormSchema';
import { formatUsd } from '@/lib/formatMoney';

interface InvestmentTrackingSectionProps {
  form: UseFormReturn<ItemFormData>;
  isEditMode: boolean;
  investmentOpen: boolean;
  setInvestmentOpen: (open: boolean) => void;
  watchQuantity: number;
  watchCostBasis: string | undefined;
  costTrackingMode: 'same' | 'individual';
  setCostTrackingMode: (mode: 'same' | 'individual') => void;
  costBasisMode: 'per_item' | 'total';
  setCostBasisMode: (mode: 'per_item' | 'total') => void;
  individualCosts: string[];
  setIndividualCosts: (costs: string[]) => void;
  individualDates: string[];
  setIndividualDates: (dates: string[]) => void;
}

export function InvestmentTrackingSection({
  form,
  isEditMode,
  investmentOpen,
  setInvestmentOpen,
  watchQuantity,
  watchCostBasis,
  costTrackingMode,
  setCostTrackingMode,
  costBasisMode,
  setCostBasisMode,
  individualCosts,
  setIndividualCosts,
  individualDates,
  setIndividualDates,
}: InvestmentTrackingSectionProps) {
  return (
    <div className={cn('animate-fade-in', isEditMode ? 'order-3' : '')}>
      <Collapsible open={investmentOpen} onOpenChange={setInvestmentOpen}>
        <CollapsibleTrigger
          type="button"
          className={cn(
            'group w-full flex items-center justify-center gap-2 px-2 py-2 -mx-2 rounded-md text-center transition-colors',
            'text-white/60 hover:text-white hover:bg-white/[0.04]',
            '[&[data-state=open]>span.plus]:rotate-45'
          )}
        >
          <span className="text-sm font-medium">Investment Tracking</span>
          <span className="plus w-5 h-5 flex items-center justify-center text-lg leading-none text-white/50 group-hover:text-white transition-transform">+</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3 space-y-3">
          <p className="text-[11px] text-white/40 leading-relaxed">
            Purchase date determines long-term vs. short-term gains on your tax exports. Cost is used for profit calculations.
          </p>
          {/* Date + Cost row (single qty) — stacks on mobile */}
          <FormField
            control={form.control}
            name="purchase_date"
            render={({ field }) => {
              const dateStr = field.value && field.value.length >= 10 ? field.value.substring(0, 10) : '';
              const selectedDate = dateStr ? parseISO(dateStr) : undefined;
              return (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground/60 z-10 flex items-center justify-center w-4">
                        <CalendarIcon className="h-4 w-4" />
                      </span>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            className={cn(
                              'w-full h-14 pl-9 pr-3 justify-start text-left font-normal rounded-md text-base field-surface field-border hover:border-white/20 hover:bg-transparent',
                              selectedDate ? 'text-foreground' : 'text-muted-foreground/60'
                            )}
                          >
                            <span className="flex-1 truncate">
                              {selectedDate ? format(selectedDate, 'MMM d, yyyy') : 'Purchase Date (optional)'}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-chrome border-white/[0.08]" align="start">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(d) => field.onChange(d ? format(d, 'yyyy-MM-dd') : '')}
                            disabled={(d) => d > new Date() || d < new Date('1990-01-01')}
                            initialFocus
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </FormControl>
                </FormItem>
              );
            }}
          />

          {/* Cost Tracking Section */}
          <div className="space-y-3">
            {/* Toggle groups row */}
            {watchQuantity > 1 && (
              <div className="w-full">
                {/* Same for all / Different each toggle */}
                <div className="flex gap-1 p-1 rounded-lg bg-black/30 border border-white/[0.08]">
                  <button
                    type="button"
                    onClick={() => {
                      setCostTrackingMode('same');
                      setIndividualCosts([]);
                      setIndividualDates([]);
                    }}
                    className={cn(
                      'flex-1 px-4 py-2 rounded-md text-xs font-medium transition-all duration-200 border',
                      costTrackingMode === 'same'
                        ? 'bg-gradient-to-r from-white/[0.12] to-white/[0.06] text-white border-white/30 shadow-[0_0_10px_rgba(255,255,255,0.06)]'
                        : 'text-white/50 hover:text-white/70 border-transparent'
                    )}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Same for all
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCostTrackingMode('individual');
                      setIndividualCosts(Array(watchQuantity).fill(''));
                      setIndividualDates(Array(watchQuantity).fill(''));
                    }}
                    className={cn(
                      'flex-1 px-4 py-2 rounded-md text-xs font-medium transition-all duration-200 border',
                      costTrackingMode === 'individual'
                        ? 'bg-gradient-to-r from-white/[0.12] to-white/[0.06] text-white border-white/30 shadow-[0_0_10px_rgba(255,255,255,0.06)]'
                        : 'text-white/50 hover:text-white/70 border-transparent'
                    )}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Different each
                  </button>
                </div>
              </div>
            )}

            {/* Same cost for all mode - with Per Item/Total inline on desktop */}
            {(watchQuantity === 1 || costTrackingMode === 'same') && (
              <FormField
                control={form.control}
                name="cost_basis"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <div className="md:flex-1">
                        <FormControl>
                          <div className="relative">
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground/60 z-10 flex items-center justify-center w-4">
                              $
                            </span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Cost"
                              className="h-14 pl-9 text-base field-surface field-border placeholder:text-muted-foreground/60"
                              onWheel={(e) => e.currentTarget.blur()}
                              {...field}
                            />
                          </div>
                        </FormControl>
                      </div>

                      {/* Per Item / Total toggle - inline on desktop, below on mobile */}
                      {watchQuantity > 1 && costTrackingMode === 'same' && (
                        <div className="flex gap-1 p-1 rounded-lg bg-black/30 border border-white/[0.08] w-full md:w-auto md:shrink-0">
                          <button
                            type="button"
                            onClick={() => setCostBasisMode('per_item')}
                            className={cn(
                              'flex-1 md:flex-initial px-4 py-2 rounded-md text-xs font-medium transition-all duration-200 border',
                              costBasisMode === 'per_item'
                                ? 'bg-gradient-to-r from-white/[0.12] to-white/[0.06] text-white border-white/30 shadow-[0_0_10px_rgba(255,255,255,0.06)]'
                                : 'text-white/50 hover:text-white/70 border-transparent'
                            )}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Per Item
                          </button>
                          <button
                            type="button"
                            onClick={() => setCostBasisMode('total')}
                            className={cn(
                              'flex-1 md:flex-initial px-4 py-2 rounded-md text-xs font-medium transition-all duration-200 border',
                              costBasisMode === 'total'
                                ? 'bg-gradient-to-r from-white/[0.12] to-white/[0.06] text-white border-white/30 shadow-[0_0_10px_rgba(255,255,255,0.06)]'
                                : 'text-white/50 hover:text-white/70 border-transparent'
                            )}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Total
                          </button>
                        </div>
                      )}
                    </div>

                    {watchQuantity > 1 && watchCostBasis && Number(watchCostBasis) > 0 && (
                      <FormDescription className="text-xs">
                        {costBasisMode === 'total' ? (
                          <span className="text-success font-medium">
                            → ${(Number(watchCostBasis) / watchQuantity).toFixed(2)} per item
                          </span>
                        ) : (
                          <span className="text-foreground font-medium">
                            Total: ${(Number(watchCostBasis) * watchQuantity).toFixed(2)}
                          </span>
                        )}
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            )}

            {/* Individual costs mode - per-unit inputs with dates */}
            {watchQuantity > 1 && costTrackingMode === 'individual' && (
              <div className="space-y-3">
                {/* Header row */}
                <div className="grid grid-cols-[2rem_1fr_auto] gap-2 text-xs text-muted-foreground font-medium">
                  <span className="text-center">#</span>
                  <span>Cost</span>
                  <span>Date (optional)</span>
                </div>

                {/* Per-unit rows */}
                <div className="space-y-2">
                  {individualCosts.map((cost, idx) => (
                    <div key={idx} className="grid grid-cols-[2rem_1fr_auto] gap-2 items-center">
                      <span className="text-xs text-muted-foreground text-center">{idx + 1}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="$0.00"
                        value={cost}
                        onChange={(e) => {
                          const updated = [...individualCosts];
                          updated[idx] = e.target.value;
                          setIndividualCosts(updated);
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="h-9"
                      />
                      <Input
                        type="date"
                        max={new Date().toISOString().split('T')[0]}
                        min="1990-01-01"
                        value={individualDates[idx] || ''}
                        onChange={(e) => {
                          const updated = [...individualDates];
                          updated[idx] = e.target.value;
                          setIndividualDates(updated);
                        }}
                        className="h-9 w-[8.5rem] sm:w-auto"
                      />
                    </div>
                  ))}
                </div>

                {/* Summary */}
                {(() => {
                  const validCosts = individualCosts
                    .map((c) => parseFloat(c))
                    .filter((c) => !isNaN(c) && c > 0);
                  if (validCosts.length === 0) return null;
                  const total = validCosts.reduce((sum, c) => sum + c, 0);
                  const avg = total / validCosts.length;
                  const missing = watchQuantity - validCosts.length;
                  return (
                    <div className="flex items-center justify-between text-xs p-2 rounded bg-muted/30">
                      <div className="space-x-3">
                        <span>Avg: <span className="text-primary font-medium">{formatUsd(avg)}</span></span>
                        <span>Total: <span className="text-foreground font-medium">{formatUsd(total)}</span></span>
                      </div>
                      {missing > 0 && <span className="text-warning">{missing} missing</span>}
                    </div>
                  );
                })()}

                {/* Individual tracking notice */}
                <p className="text-[10px] text-muted-foreground/70 italic">
                  Each unit's cost will be tracked individually for accurate profit calculation when sold.
                </p>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
